import { BYOKProvider } from '@developerz/domain';
import { createEffect, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { LlmPref } from '../lib/my-llm-pref';
import { providerLabel } from './KeyCard';
import { Panel } from './Panel';
import { Select } from './Select';
import { Button, buttonClass } from './ui/Button';

const PROVIDERS = Object.values(BYOKProvider);

// Sentinel for the "any provider" option — an empty select value maps to the
// preference's `provider: null` (keep the org's provider, override only the model).
const ANY = '';

/** The provider control's id. One card per page, so one id is enough. */
const PROVIDER_ID = 'llm-pref-provider';

export type LlmPrefCardProps = {
  readonly pref: LlmPref | null | undefined;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSave: (input: { provider: string | null; model: string }) => void;
  readonly onClear: () => void;
};

/**
 * Per-user default-model panel (epic #635) — presenter only; the container
 * (`MyKeys`) owns fetching + mutations. Sets/clears the model a member's work
 * runs on while still signing it with the org's provider + key. Controls sit on
 * one md: row per the mock; labels are aria-only. Form last (happy-dom #481).
 */
export function LlmPrefCard(props: LlmPrefCardProps): JSX.Element {
  const [provider, setProvider] = createSignal<string>(ANY);
  const [model, setModel] = createSignal('');

  // Seed the form from server truth whenever it (re)loads, so a saved
  // preference is editable in place rather than starting blank.
  createEffect(() => {
    const p = props.pref;
    setProvider(p?.provider ?? ANY);
    setModel(p?.model ?? '');
  });

  const submit = (e: Event): void => {
    e.preventDefault();
    const m = model().trim();
    if (m === '') return;
    props.onSave({ provider: provider() === ANY ? null : provider(), model: m });
  };

  return (
    <Panel title={t('my_keys.pref_title')} class="llm-pref">
      <p class="card__desc keys-panel__desc">{t('my_keys.pref_subtitle')}</p>

      <form class="keys-form" onSubmit={submit}>
        <div class="keys-form__row">
          <label id={`${PROVIDER_ID}-label`} for={PROVIDER_ID} class="sr-only">
            {t('my_keys.pref_provider')}
          </label>
          <Select
            a11y={{ id: PROVIDER_ID }}
            label={t('my_keys.pref_provider')}
            block
            value={provider()}
            options={[
              { value: ANY, label: t('my_keys.pref_any_provider') },
              ...PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) })),
            ]}
            onChange={setProvider}
          />
          <input
            class="input"
            aria-label={t('my_keys.pref_model')}
            value={model()}
            onInput={(e) => setModel(e.currentTarget.value)}
            placeholder={t('account_keys.model_placeholder')}
            required
          />
          <Show when={props.pref}>
            <Button variant="outline" disabled={props.busy} onClick={() => props.onClear()}>
              {t('my_keys.pref_clear')}
            </Button>
          </Show>
          <button
            type="submit"
            class={buttonClass()}
            disabled={props.busy || model().trim() === ''}
          >
            {props.busy ? t('my_keys.pref_saving') : t('my_keys.pref_save')}
          </button>
        </div>
        <Show when={props.error}>
          <p class="field__error" role="alert">
            {props.error}
          </p>
        </Show>
      </form>
    </Panel>
  );
}
